import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Snackbar,
  Stack
} from '@mui/material';
import { SUPPORT_CREATE } from '../config/Config';
import api from '../services/api';

const ISSUE_OPTIONS = [
  { value: 'LOGIN', label: 'Login / Account' },
  { value: 'PAYMENT', label: 'Payment / Subscription' },
  { value: 'CONTENT', label: 'Content issue' },
  { value: 'OTHER', label: 'Other' },
  { value: 'FEEDBACK', label: 'Feedback' },
  { value: 'CHAMPIONS_PROGRAM', label: 'Champions Program' },
];

const SupportDialogFixed = forwardRef(({ open, onClose, presetEmail = '', presetData = null }, ref) => {
  const [form, setForm] = useState({
    user_email: presetEmail,
    issue_type: 'OTHER',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    setFormData: (data) => {
      setForm(prev => ({
        ...prev,
        ...data
      }));
    }
  }));

  useEffect(() => {
    setForm((prev) => ({ ...prev, user_email: presetEmail }));
  }, [presetEmail]);

  useEffect(() => {
    if (presetData) {
      setForm(prev => ({
        ...prev,
        ...presetData
      }));
    }
  }, [presetData]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleClose = () => {
    setSnackbar({ ...snackbar, open: false });
    onClose();
  };

  const validate = () => {
    const err = {};
    if (!form.user_email) err.user_email = 'Email required';
    if (!form.message) err.message = 'Message required';
    else if (form.message.length < 10) err.message = 'Message must be at least 10 characters';
    else if (form.message.length > 100) err.message = 'Max 100 characters';
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      await api.post(SUPPORT_CREATE, form);
      setSnackbar({ open: true, message: 'Support request submitted successfully!', severity: 'success' });
      handleClose();
    } catch (error) {
      console.error('Error submitting support request:', error);
      setSnackbar({ open: true, message: 'Failed to submit support request. Please try again.', severity: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Contact Support</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Your email"
              fullWidth
              value={form.user_email}
              onChange={handleChange('user_email')}
              error={!!errors.user_email}
              helperText={presetEmail ? 'Email pre-populated from your account (cannot be changed)' : errors.user_email}
              disabled={!!presetEmail}
              InputProps={{
                readOnly: !!presetEmail,
              }}
              aria-label={presetEmail ? 'Your email (pre-populated from account)' : 'Your email'}
            />
            <FormControl fullWidth>
              <InputLabel id="issue-type-label">Related to</InputLabel>
              <Select
                labelId="issue-type-label"
                label="Related to"
                value={form.issue_type}
                onChange={handleChange('issue_type')}
                disabled={form.issue_type === 'CHAMPIONS_PROGRAM'}
              >
                {ISSUE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
               </Select>
               {form.issue_type === 'CHAMPIONS_PROGRAM' && (
                 <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                   Category cannot be changed for Champions Program inquiries
                 </Typography>
               )}
             </FormControl>
            <TextField
              label="Describe the issue (max 100 chars) *"
              multiline
              rows={3}
              fullWidth
              value={form.message}
              onChange={handleChange('message')}
              error={!!errors.message}
              helperText={errors.message || 'Minimum 10 characters required'}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" type="submit">
            Send
          </Button>
        </DialogActions>
      </form>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
});

export default SupportDialogFixed;
